import type { ReactElement } from 'react';
import {
    AdSideBarLayout as ZDSAdSideBarLayout,
    InformationLayout as ZDSInformationLayout,
    FullwidthHeroLayout as ZDSFullwidthHeroLayout,
    FullWidthTopLayout as ZDSFullwidthTopLayout,
    PageBodyLayout as ZDSPageBodyLayout
} from '@zoopla-eng/zds-components/page';
import { Header } from '@zoopla/header';
import { Footer } from '@zoopla/footer';
import { Box } from '@zoopla-eng/zds-components/box';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { ColouredCard } from '@zoopla-eng/zds-components/coloured-card';
import { CardTitle } from '@zoopla-eng/zds-components/headings';
import { Link } from '@zoopla-eng/zds-components/link';
import { AdScripts } from '@zoopla/adverts/dfp';
import { Banner } from '@zoopla/layout/consent';
import dynamic from 'next/dynamic';

const GoogleOneTap = dynamic<Record<string, unknown>>(
    () =>
        import('@zoopla/authentication/modal').then(
            (module) => module.GoogleOneTap
        ),
    {
        ssr: false
    }
);

const stackProps = {
    rowGap: '$S400',
    '@L100': {
        flexFlow: 'row'
    }
};

const BottomRail = () => (
    <Box py="$M300" bpL100={{ py: '$L200' }} data-testid="layout-bottom-rail">
        <Stack {...stackProps}>
            <ColouredCard backgroundColor="$graphicPriceLight">
                <Box p="$S500">
                    <Box pb="$S400">
                        <CardTitle>How much is my home worth?</CardTitle>
                    </Box>
                    <Link href="/home-values/">Check price estimate</Link>
                </Box>
            </ColouredCard>
            <ColouredCard backgroundColor="$graphicBackgroundAccentDark">
                <Box p="$S500">
                    <Box pb="$S400">
                        <CardTitle>Get closer to what matters</CardTitle>
                    </Box>
                    <Link href="/travel-time/">
                        Search homes by travel time
                    </Link>
                </Box>
            </ColouredCard>
            <ColouredCard backgroundColor="$graphicImprovingLight">
                <Box p="$S500">
                    <Box pb="$S400">
                        <CardTitle>Find an agent</CardTitle>
                    </Box>
                    <Link href="/find-agents/">Look for local agents</Link>
                </Box>
            </ColouredCard>
        </Stack>
    </Box>
);

function GlobalWrapper({ children }: { children: ReactElement }) {
    return (
        <>
            <AdScripts />
            <Banner />
            {children}
            <GoogleOneTap />
        </>
    );
}

interface AdSideBarLayoutProps {
    main: ReactElement;
    adverts: ReactElement;
}

export function AdSideBarLayout({ main, adverts }: AdSideBarLayoutProps) {
    return (
        <GlobalWrapper>
            <ZDSAdSideBarLayout
                header={<Header />}
                footer={<Footer />}
                bottomRail={<BottomRail />}
                main={main}
                adverts={adverts}
            />
        </GlobalWrapper>
    );
}

interface InformationLayoutProps {
    main: ReactElement;
    breadcrumbs?: ReactElement;
}

export function InformationLayout({
    main,
    breadcrumbs
}: InformationLayoutProps) {
    return (
        <GlobalWrapper>
            <ZDSInformationLayout
                breadcrumbs={breadcrumbs}
                header={<Header />}
                footer={<Footer />}
                bottomRail={<BottomRail />}
                main={main}
            />
        </GlobalWrapper>
    );
}

interface FullwidthHeroLayoutProps {
    heroContent: ReactElement;
    heroBackgroundColor: string;
    content: ReactElement;
}

export function FullwidthHeroLayout({
    heroContent,
    heroBackgroundColor,
    content
}: FullwidthHeroLayoutProps) {
    return (
        <GlobalWrapper>
            <ZDSFullwidthHeroLayout
                header={<Header />}
                footer={<Footer />}
                heroContent={heroContent}
                heroBackgroundColor={heroBackgroundColor}
                content={content}
            />
        </GlobalWrapper>
    );
}

export function FullwidthHeroLayoutNoBottomRail({
    heroContent,
    heroBackgroundColor,
    content
}: FullwidthHeroLayoutProps) {
    return (
        <GlobalWrapper>
            <ZDSFullwidthHeroLayout
                header={<Header />}
                footer={<Footer />}
                heroContent={heroContent}
                heroBackgroundColor={heroBackgroundColor}
                content={content}
            />
        </GlobalWrapper>
    );
}

interface FullWidthTopLayoutProps {
    topRail: ReactElement;
    main: ReactElement;
}

export function FullwidthTopLayout({ topRail, main }: FullWidthTopLayoutProps) {
    return (
        <GlobalWrapper>
            <ZDSFullwidthTopLayout
                header={<Header />}
                footer={<Footer />}
                bottomRail={<BottomRail />}
                topRail={topRail}
                main={main}
            />
        </GlobalWrapper>
    );
}

export function FullwidthTopLayoutNoBottomRail({
    topRail,
    main
}: FullWidthTopLayoutProps) {
    return (
        <GlobalWrapper>
            <ZDSFullwidthTopLayout
                header={<Header />}
                footer={<Footer />}
                bottomRail={<div />}
                topRail={topRail}
                main={main}
            />
        </GlobalWrapper>
    );
}

interface PageBodyLayoutProps {
    main: ReactElement;
}

export function PageBodyLayout({ main }: PageBodyLayoutProps) {
    return (
        <GlobalWrapper>
            <ZDSPageBodyLayout bottomRail={<BottomRail />} main={main} />
        </GlobalWrapper>
    );
}

function AuthenticationWrapper({ children }: { children: ReactElement }) {
    return (
        <>
            <AdScripts />
            <Banner />
            {children}
        </>
    );
}

interface AuthenticationLayoutProps {
    main: ReactElement;
}

export function AuthenticationLayout({ main }: AuthenticationLayoutProps) {
    return (
        <AuthenticationWrapper>
            <ZDSPageBodyLayout bottomRail={<div />} main={main} />
        </AuthenticationWrapper>
    );
}
